/**
 * Conexión con el banco (Enable Banking) en la pestaña Cuentas: lógica pura.
 */
import * as monthUtils from '@actual-app/core/shared/months';

type CuentaBanco = {
  account_id?: string | null;
  account_sync_source?: string | null;
  bank_sync_status?: string | null;
  last_sync?: string | null;
};

/** ¿Está la cuenta enlazada a un banco? */
export function estaEnlazada(c: CuentaBanco): boolean {
  return !!c.account_id && !!c.account_sync_source;
}

export type EstadoBanco =
  | { tipo: 'sin-banco' }
  | { tipo: 'ok' }
  | { tipo: 'sincronizando' }
  | { tipo: 'reconectar' }
  | { tipo: 'mas-tarde' }
  | { tipo: 'cuenta-perdida' }
  | { tipo: 'error' };

/**
 * Estado amable de la sincronización a partir de `bank_sync_status` (el mismo
 * que usa `AccountSyncCheck` de Actual): «reconectar» cuando caduca el
 * permiso, «más tarde» con límites o esperas del banco…
 */
export function estadoBanco(c: CuentaBanco): EstadoBanco {
  if (!estaEnlazada(c)) {
    return { tipo: 'sin-banco' };
  }
  switch (c.bank_sync_status) {
    case null:
    case undefined:
    case 'ok':
      return { tipo: 'ok' };
    case 'pending':
    case 'sync-requested':
      return { tipo: 'sincronizando' };
    case 'reauth-required':
      return { tipo: 'reconectar' };
    case 'rate-limit-exceeded':
    case 'timed-out':
      return { tipo: 'mas-tarde' };
    case 'account-missing':
      return { tipo: 'cuenta-perdida' };
    default:
      return { tipo: 'error' };
  }
}

/**
 * Inicio de sincronización por defecto al enlazar una cuenta que ya tiene
 * movimientos (p. ej. importados de YNAB): el día siguiente al último, para no
 * traer otra vez lo que ya está. Nunca antes de hace 89 días (los bancos no
 * dan más de 90) ni después de hoy. Sin movimientos: hace 89 días (lo de
 * Actual).
 */
export function inicioSincronizacion(
  ultimaFecha: string | null | undefined,
  hoy: string = monthUtils.currentDay(),
): string {
  const tope = monthUtils.subDays(hoy, 89);
  if (!ultimaFecha) {
    return tope;
  }
  const siguiente = monthUtils.addDays(ultimaFecha, 1);
  if (siguiente > hoy) {
    return hoy;
  }
  return siguiente < tope ? tope : siguiente;
}

type CuentaParaAuto = CuentaBanco & {
  closed?: boolean | number | null;
  tombstone?: boolean | number | null;
};

/** Horas mínimas entre sincronizaciones automáticas al abrir la app. */
export const HORAS_SYNC_AUTO = 6;

/**
 * ¿Toca sincronizar el banco solo, sin pedirlo? Sí cuando alguna cuenta
 * enlazada y abierta lleva más de `horas` sin sincronizar (o nunca lo ha
 * hecho). Las que piden reconectar o ya no existen en el banco no cuentan:
 * sincronizarlas fallaría igual y avisaría en cada apertura.
 */
export function tocaSincronizarSolo(
  cuentas: CuentaParaAuto[],
  ahora: number = Date.now(),
  horas: number = HORAS_SYNC_AUTO,
): boolean {
  const limite = ahora - horas * 60 * 60 * 1000;
  return cuentas.some(c => {
    if (!estaEnlazada(c) || c.closed || c.tombstone) {
      return false;
    }
    const estado = estadoBanco(c).tipo;
    if (estado === 'reconectar' || estado === 'cuenta-perdida') {
      return false;
    }
    const ultima = Number(c.last_sync);
    return !c.last_sync || !Number.isFinite(ultima) || ultima < limite;
  });
}

/**
 * ¿Ya hubo hoy un intento automático? Se guarda el día del último intento
 * (aaaa-mm-dd, hora local) y solo se intenta una vez al día: aunque una
 * cuenta siga sin sincronizar (error del banco, cuenta sin movimientos…),
 * no se reintenta en cada apertura. Para más, deslizar en Cuentas.
 */
export function yaSincronizadoHoy(
  ultimoDia: string | null | undefined,
  hoy: string,
): boolean {
  return ultimoDia === hoy;
}

/**
 * «Última sincronización» de la pestaña Cuentas (B7 · H52): la más reciente
 * de las cuentas abiertas enlazadas. `hay` dice si hay alguna enlazada (si no,
 * la línea no sale); `ultima` es `null` si ninguna se ha sincronizado aún.
 */
export function ultimaSincronizacion(cuentas: readonly CuentaParaAuto[]): {
  hay: boolean;
  ultima: number | null;
} {
  let hay = false;
  let ultima: number | null = null;
  for (const c of cuentas) {
    if (!estaEnlazada(c) || c.closed || c.tombstone) {
      continue;
    }
    hay = true;
    const ms = Number(c.last_sync);
    if (c.last_sync && Number.isFinite(ms) && (ultima == null || ms > ultima)) {
      ultima = ms;
    }
  }
  return { hay, ultima };
}

/** Qué le pasa al banco, en claro, para la banda de aviso (B7 · H53). */
export type AvisoBanco =
  | 'reautorizar'
  | 'mas-tarde'
  | 'cuenta-perdida'
  | 'atencion'
  | 'error';

/**
 * Traduce el error de `getFailedSyncError` (o el detallado del último
 * intento, `useFailedAccounts`) a un aviso: tipo de aviso y si «Reconectar»
 * lo arregla (permiso caducado o cuenta que el banco ya no encuentra).
 */
export function avisoBanco(error: { type: string; code: string }): {
  aviso: AvisoBanco;
  reconectar: boolean;
} {
  const type = error.type.toUpperCase();
  const code = error.code.toUpperCase();
  if (
    (type === 'ITEM_ERROR' && code === 'ITEM_LOGIN_REQUIRED') ||
    type === 'INVALID_ACCESS_TOKEN' ||
    (type === 'INVALID_INPUT' && code === 'INVALID_ACCESS_TOKEN')
  ) {
    return { aviso: 'reautorizar', reconectar: true };
  }
  if (type === 'ITEM_ERROR' && code === 'NO_ACCOUNTS') {
    return { aviso: 'cuenta-perdida', reconectar: true };
  }
  switch (type) {
    case 'RATE_LIMIT_EXCEEDED':
    case 'TIMED_OUT':
      return { aviso: 'mas-tarde', reconectar: false };
    case 'ACCOUNT_MISSING':
      return { aviso: 'cuenta-perdida', reconectar: true };
    case 'ACCOUNT_NEEDS_ATTENTION':
      return { aviso: 'atencion', reconectar: false };
    default:
      return { aviso: 'error', reconectar: false };
  }
}

/**
 * Saldo del banco frente al de Rumbo (B7 · H51), en céntimos. `null` si el
 * banco no ha dado saldo (`balance_current`): sin comparar no se dice que
 * cuadra.
 */
export function comparaConBanco(
  saldoBanco: number | null | undefined,
  saldoRumbo: number,
): { cuadra: boolean; diferencia: number } | null {
  if (saldoBanco == null || !Number.isFinite(saldoBanco)) {
    return null;
  }
  const diferencia = saldoBanco - saldoRumbo;
  return { cuadra: diferencia === 0, diferencia };
}
